import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import type { SIFTVehicle, UserVinStatus } from '../../types/sift';
import { USER_STATUS_OPTIONS, USER_STATUS_DISPLAY_DEFAULT, VIN_STATUS_REASON_MAX } from '../../types/sift';
import { VehicleInsightsModal } from '../modal/VehicleInsightsModal';
import { siftApiClient } from '../../api/siftApiClient';
import { apiVehicleToSIFTVehicle } from '../../api/dataAdapter';
import './VehicleDataTable.css';

// Column definitions with default widths - all columns are now filterable
const COLUMNS = [
  { key: 'user_status', label: 'Status', defaultWidth: 110, filterable: true },
  { key: 'seller_id', label: 'Seller ID', defaultWidth: 100, filterable: true },
  { key: 'seller_name', label: 'Seller Name', defaultWidth: 150, filterable: true },
  { key: 'vin', label: 'VIN', defaultWidth: 170, filterable: true },
  { key: 'year', label: 'Year', defaultWidth: 60, filterable: true },
  { key: 'make', label: 'Make', defaultWidth: 80, filterable: true },
  { key: 'model', label: 'Model', defaultWidth: 100, filterable: true },
  { key: 'trim', label: 'Trim', defaultWidth: 100, filterable: true },
  { key: 'list_status', label: 'List Status', defaultWidth: 90, filterable: true },
  { key: 'inspect_status', label: 'Inspect Status', defaultWidth: 110, filterable: true },
  { key: 'cr_grade', label: 'CR Grade', defaultWidth: 80, filterable: true },
  { key: 'inspection_channel', label: 'Inspect Channel', defaultWidth: 120, filterable: true },
  { key: 'inspect_time', label: 'Inspect Date', defaultWidth: 100, filterable: true },
  { key: 'channel', label: 'Channel', defaultWidth: 80, filterable: true },
  { key: 'facilitation', label: 'Facilitation', defaultWidth: 100, filterable: true },
  { key: 'site_location', label: 'Site Location', defaultWidth: 120, filterable: true },
  { key: 'days_listed', label: 'Days Listed', defaultWidth: 90, filterable: true },
  { key: 'days_expired', label: 'Days Expired', defaultWidth: 95, filterable: true },
  { key: 'vdp_url', label: 'VDP', defaultWidth: 70, filterable: false },
  { key: 'data_source', label: 'Data Source', defaultWidth: 100, filterable: true },
  { key: 'territory_city', label: 'City', defaultWidth: 100, filterable: true },
  { key: 'territory_state', label: 'State', defaultWidth: 60, filterable: true },
  { key: 'territory_zip', label: 'Zip', defaultWidth: 70, filterable: true },
];

// Type for column filters
type ColumnFilters = Record<string, Set<string>>;

const COLUMN_LABEL: Record<string, string> = Object.fromEntries(
  COLUMNS.map((c) => [c.key, c.label]),
);

// Helper function to format date string to MM/DD/YYYY
const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
  } catch {
    return '-';
  }
};

interface VehicleDataTableProps {
  vehicles?: SIFTVehicle[];
  onRowSelect: (vehicles: SIFTVehicle[]) => void;
  searchTerm?: string;
  onSearchChange?: (searchTerm: string) => void;
  onSearchClick?: () => void;
  onClearSearch?: () => void;
  sortColumns?: Array<{ key: string; order: 'asc' | 'desc' }>;
  onSortChange?: (columns: Array<{ key: string; order: 'asc' | 'desc' }>) => void;
  // Filter context for fetching column distinct values
  currentFilters?: {
    manager?: string;
    market_center?: string;
    rds_territory?: string;
    seller_name?: string;
    stage?: string;
    inspect_status?: string;
    days_listed_ranges?: string;
    days_expired_ranges?: string;
    days_since_inspection_ranges?: string;
    user_status?: string;
    search?: string;
  };
  impersonateRole?: string;
  // Column filters - lifted to parent for backend filtering
  columnFilters?: Record<string, Set<string>>;
  onColumnFiltersChange?: (filters: Record<string, Set<string>>) => void;
  // Selection props for bulk status assignment
  selectedVins?: Set<string>;
  onSelectionChange?: (selectedVins: Set<string>) => void;
  // Status assignment callback
  onStatusAssign?: (vins: string[], status: UserVinStatus, reasonText?: string | null) => void;
  /** Mia's "Where to focus" tab in the VIN modal (RDS-3.5). */
  miaEnabled?: boolean;
}

export const VehicleDataTable: React.FC<VehicleDataTableProps> = ({
  vehicles: providedVehicles,
  searchTerm: externalSearchTerm,
  onSearchChange,
  onSearchClick,
  onClearSearch,
  sortColumns: externalSortColumns = [],
  onSortChange,
  currentFilters,
  impersonateRole,
  columnFilters: externalColumnFilters,
  onColumnFiltersChange,
  selectedVins: externalSelectedVins,
  onSelectionChange,
  onStatusAssign,
  miaEnabled = false,
}) => {
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [vehicles, setVehicles] = useState<SIFTVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState<SIFTVehicle | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingVehicleDetails, setIsLoadingVehicleDetails] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  
  // Selection state for bulk status assignment
  const [internalSelectedVins, setInternalSelectedVins] = useState<Set<string>>(new Set());
  const selectedVins = externalSelectedVins ?? internalSelectedVins;
  const setSelectedVins = useCallback((updater: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    const newSelection = typeof updater === 'function' ? updater(selectedVins) : updater;
    if (onSelectionChange) {
      onSelectionChange(newSelection);
    } else {
      setInternalSelectedVins(newSelection);
    }
  }, [selectedVins, onSelectionChange]);
  
  // Status assignment modal state
  const [showStatusModal, setShowStatusModal] = useState(false);
  // RDS-2.3: optional reason applied with the assigned status, and the reasons on screen.
  const [statusReason, setStatusReason] = useState('');
  const [statusReasons, setStatusReasons] = useState<Record<string, string>>({});
  
  // Column filter state - use external if provided, otherwise internal
  const [internalColumnFilters, setInternalColumnFilters] = useState<ColumnFilters>({});
  const columnFilters = externalColumnFilters ?? internalColumnFilters;
  const setColumnFilters = useCallback((updater: ColumnFilters | ((prev: ColumnFilters) => ColumnFilters)) => {
    const newFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
    if (onColumnFiltersChange) {
      onColumnFiltersChange(newFilters);
    } else {
      setInternalColumnFilters(newFilters);
    }
  }, [columnFilters, onColumnFiltersChange]);
  
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(null);
  const [filterSearchTerm, setFilterSearchTerm] = useState<string>('');
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  
  // Pending filter state - tracks user selections before "Apply" is clicked
  const [pendingFilter, setPendingFilter] = useState<Set<string> | null>(null);
  const [pendingSelectAll, setPendingSelectAll] = useState<boolean>(true);
  
  // Column distinct values cache (fetched from API)
  const [columnDistinctValues, setColumnDistinctValues] = useState<Record<string, string[]>>({});
  const [columnTruncatedInfo, setColumnTruncatedInfo] = useState<Record<string, { truncated: boolean; total_count: number }>>({});
  const [loadingColumnValues, setLoadingColumnValues] = useState<string | null>(null);
  
  // Column widths state - initialize from defaults
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const widths: Record<string, number> = {};
    COLUMNS.forEach(col => { widths[col.key] = col.defaultWidth; });
    return widths;
  });
  
  // Refs for resize tracking
  const resizingColumn = useRef<string | null>(null);
  const startX = useRef<number>(0);
  const startWidth = useRef<number>(0);

  // Column resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    resizingColumn.current = columnKey;
    startX.current = e.clientX;
    startWidth.current = columnWidths[columnKey];
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!resizingColumn.current) return;
    const diff = e.clientX - startX.current;
    const newWidth = Math.max(40, startWidth.current + diff);
    setColumnWidths(prev => ({
      ...prev,
      [resizingColumn.current!]: newWidth
    }));
  }, []);

  const handleResizeEnd = useCallback(() => {
    resizingColumn.current = null;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [handleResizeMove]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [handleResizeMove, handleResizeEnd]);

  // Use external search term if provided, otherwise use internal
  const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;
  const handleSearchChange = (value: string) => {
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearchTerm(value);
    }
  };

  // Use provided vehicles from parent component
  useEffect(() => {
    if (providedVehicles) {
      setVehicles(providedVehicles);
      setLoading(false);
    }
  }, [providedVehicles]);

  // Clear cached column values when parent filters change
  useEffect(() => {
    setColumnDistinctValues({});
    setColumnTruncatedInfo({});
  }, [currentFilters, impersonateRole]);

  // Use provided vehicles from parent component
  const displayVehicles = providedVehicles || vehicles;

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setActiveFilterColumn(null);
        setFilterSearchTerm('');
      }
    };

    if (activeFilterColumn) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeFilterColumn]);

  // Helper to get vehicle value by column key
  const getVehicleValue = useCallback((vehicle: SIFTVehicle, key: string): string | number | null => {
    switch (key) {
      case 'seller_id': return vehicle.seller_id;
      case 'seller_name': return vehicle.seller_name;
      case 'vin': return vehicle.vin;
      case 'year': return vehicle.model_year;
      case 'make': return vehicle.make;
      case 'model': return vehicle.model;
      case 'trim': return vehicle.trim;
      case 'list_status': return vehicle.list_status;
      case 'inspect_status': return vehicle.inspect_status;
      case 'cr_grade': return vehicle.cr_grade;
      case 'inspection_channel': return vehicle.inspection_channel;
      case 'inspect_time': return vehicle.inspect_time;
      case 'channel': return vehicle.channel;
      case 'facilitation': return vehicle.facilitation;
      case 'site_location': return vehicle.site_location;
      case 'days_listed': return vehicle.list_days;
      case 'days_expired': return vehicle.days_expired;
      case 'data_source': return vehicle.data_source;
      case 'territory_city': return vehicle.territory_city;
      case 'territory_state': return vehicle.territory_state;
      case 'territory_zip': return vehicle.territory_zip;
      default: return null;
    }
  }, []);

  // Get unique values for a column from the display data
  const getUniqueColumnValues = useCallback((columnKey: string): string[] => {
    const values = new Set<string>();
    displayVehicles.forEach(vehicle => {
      const value = getVehicleValue(vehicle, columnKey);
      if (value !== null && value !== undefined && value !== '') {
        values.add(String(value));
      }
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [displayVehicles, getVehicleValue]);

  // Fetch column distinct values from API
  const fetchColumnDistinctValues = useCallback(async (columnKey: string, valueSearch?: string) => {
    // Don't fetch if we already have values for this column (and no search term)
    if (columnDistinctValues[columnKey] && !valueSearch) {
      return;
    }
    
    setLoadingColumnValues(columnKey);
    try {
      const response = await siftApiClient.getColumnDistinctValues(
        columnKey,
        currentFilters,
        impersonateRole,
        valueSearch
      );
      
      if (valueSearch) {
        // For search results, merge with existing values (don't replace)
        setColumnDistinctValues(prev => {
          const existingValues = prev[columnKey] || [];
          const newValues = response.values.filter(v => !existingValues.includes(v));
          return {
            ...prev,
            [columnKey]: [...existingValues, ...newValues].sort((a, b) => 
              a.localeCompare(b, undefined, { numeric: true })
            )
          };
        });
      } else {
        setColumnDistinctValues(prev => ({
          ...prev,
          [columnKey]: response.values
        }));
        setColumnTruncatedInfo(prev => ({
          ...prev,
          [columnKey]: { truncated: response.truncated, total_count: response.total_count }
        }));
      }
    } catch (error) {
      console.error(`Failed to fetch distinct values for column ${columnKey}:`, error);
      // Fall back to local values from current page (only if not searching)
      if (!valueSearch) {
        const localValues = getUniqueColumnValues(columnKey);
        setColumnDistinctValues(prev => ({
          ...prev,
          [columnKey]: localValues
        }));
        setColumnTruncatedInfo(prev => ({
          ...prev,
          [columnKey]: { truncated: false, total_count: localValues.length }
        }));
      }
    } finally {
      setLoadingColumnValues(null);
    }
  }, [columnDistinctValues, currentFilters, impersonateRole, getUniqueColumnValues]);

  // Debounced search for truncated columns
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const handleFilterSearchChange = useCallback((value: string, columnKey: string, isTruncated: boolean) => {
    setFilterSearchTerm(value);
    
    // If column is truncated and user is typing, search on backend after debounce
    if (isTruncated && value.length >= 2) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        fetchColumnDistinctValues(columnKey, value);
      }, 300); // 300ms debounce
    }
  }, [fetchColumnDistinctValues]);

  // Toggle filter dropdown for a column
  const handleFilterClick = (e: React.MouseEvent, columnKey: string) => {
    e.stopPropagation();
    if (activeFilterColumn === columnKey) {
      // Closing dropdown - reset pending state
      setActiveFilterColumn(null);
      setFilterSearchTerm('');
      setPendingFilter(null);
      setPendingSelectAll(true);
    } else {
      // Opening dropdown - initialize pending state from current filter
      setActiveFilterColumn(columnKey);
      setFilterSearchTerm('');
      
      // Initialize pending state based on current filter for this column
      const currentFilter = columnFilters[columnKey];
      if (currentFilter && currentFilter.size > 0) {
        // There's an active filter - copy it to pending
        setPendingFilter(new Set(currentFilter));
        setPendingSelectAll(false);
      } else {
        // No filter - "Select All" is active
        setPendingFilter(null);
        setPendingSelectAll(true);
      }
      
      // Fetch distinct values for this column
      fetchColumnDistinctValues(columnKey);
    }
  };

  // Toggle a value in PENDING filter (doesn't apply until "Apply" is clicked)
  const handlePendingFilterToggle = (value: string) => {
    if (pendingSelectAll) {
      // User clicked a value while "Select All" is checked - do nothing
      // They need to uncheck "Select All" first
      return;
    }
    
    setPendingFilter(prev => {
      const newSet = new Set(prev || []);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      return newSet;
    });
  };

  // Toggle "Select All" in pending state
  const handlePendingSelectAllToggle = () => {
    if (pendingSelectAll) {
      // Unchecking "Select All" - start with empty selection
      setPendingSelectAll(false);
      setPendingFilter(new Set<string>());
    } else {
      // Checking "Select All" - clear pending filter
      setPendingSelectAll(true);
      setPendingFilter(null);
    }
  };

  // Apply the pending filter
  const handleApplyFilter = (columnKey: string) => {
    if (pendingSelectAll || !pendingFilter || pendingFilter.size === 0) {
      // "Select All" or no selections = remove filter for this column
      setColumnFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[columnKey];
        return newFilters;
      });
    } else {
      // Apply the pending selections
      setColumnFilters(prev => ({
        ...prev,
        [columnKey]: new Set(pendingFilter)
      }));
    }
    
    // Close dropdown
    setActiveFilterColumn(null);
    setFilterSearchTerm('');
    setPendingFilter(null);
    setPendingSelectAll(true);
  };

  // Cancel and close dropdown without applying
  const handleCancelFilter = () => {
    setActiveFilterColumn(null);
    setFilterSearchTerm('');
    setPendingFilter(null);
    setPendingSelectAll(true);
  };

  // =========================================================================
  // Checkbox Selection Handlers
  // =========================================================================
  
  // Toggle selection for a single VIN
  const handleRowCheckboxChange = useCallback((vin: string, checked: boolean) => {
    setSelectedVins(prev => {
      const newSelection = new Set(prev);
      if (checked) {
        newSelection.add(vin);
      } else {
        newSelection.delete(vin);
      }
      return newSelection;
    });
  }, [setSelectedVins]);
  
  // Toggle select all for current page
  const handleSelectAllChange = useCallback((checked: boolean) => {
    if (checked) {
      const allVins = new Set(displayVehicles.map(v => v.vin));
      setSelectedVins(allVins);
    } else {
      setSelectedVins(new Set());
    }
  }, [displayVehicles, setSelectedVins]);
  
  // Check if all vehicles on current page are selected
  const isAllSelected = useMemo(() => {
    if (displayVehicles.length === 0) return false;
    return displayVehicles.every(v => selectedVins.has(v.vin));
  }, [displayVehicles, selectedVins]);
  
  // Check if some but not all vehicles are selected
  const isSomeSelected = useMemo(() => {
    if (displayVehicles.length === 0) return false;
    const selectedCount = displayVehicles.filter(v => selectedVins.has(v.vin)).length;
    return selectedCount > 0 && selectedCount < displayVehicles.length;
  }, [displayVehicles, selectedVins]);
  
  // Handle "Assign Status" button click
  const handleAssignStatusClick = useCallback(() => {
    setShowStatusModal(true);
  }, []);
  
  // Handle status selection from modal
  const handleStatusSelect = useCallback((status: UserVinStatus) => {
    if (onStatusAssign && selectedVins.size > 0) {
      onStatusAssign(Array.from(selectedVins), status, status ? statusReason.trim() || null : null);
    }
    setShowStatusModal(false);
    setStatusReason('');
    // Optionally clear selection after assignment
    // setSelectedVins(new Set());
  }, [onStatusAssign, selectedVins, statusReason]);

  // Reasons for the rows on this page that carry a status (badge tooltip).
  const statusVinsKey = displayVehicles.filter((v) => v.user_status).map((v) => v.vin).join(',');
  useEffect(() => {
    if (!statusVinsKey) {
      setStatusReasons({});
      return;
    }
    let cancelled = false;
    siftApiClient.getVinStatusDetails(statusVinsKey.split(','))
      .then((details) => {
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const [vin, d] of Object.entries(details)) if (d.reason_text) next[vin] = d.reason_text;
        setStatusReasons(next);
      })
      .catch((e) => console.error('Failed to load VIN status reasons', e));
    return () => { cancelled = true; };
  }, [statusVinsKey]);
  
  // Get display text for user status
  const getStatusDisplayText = useCallback((status: UserVinStatus): string => {
    return status || USER_STATUS_DISPLAY_DEFAULT;
  }, []);
  
  // Get status badge class
  const getStatusBadgeClass = useCallback((status: UserVinStatus): string => {
    if (!status) return 'status-badge status-unassigned';
    switch (status) {
      case 'In Progress': return 'status-badge status-in-progress';
      case 'Done': return 'status-badge status-done';
      case 'Sold / NA': return 'status-badge status-sold-na';
      case 'Client Elect, No List': return 'status-badge status-client-elect';
      default: return 'status-badge status-unassigned';
    }
  }, []);

  // Clear all column filters
  const clearAllColumnFilters = () => {
    setColumnFilters({});
  };

  const clearColumnFilter = (columnKey: string) => {
    setColumnFilters(prev => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
  };

  const activeColumnFilters = Object.entries(columnFilters)
    .filter(([, v]) => v && v.size > 0)
    .map(([k, v]) => [k, Array.from(v)] as [string, string[]]);

  // Check if column has active filter
  const hasActiveFilter = (columnKey: string) => {
    return columnFilters[columnKey] && columnFilters[columnKey].size > 0;
  };

  // Count of active column filters
  const activeFilterCount = Object.keys(columnFilters).filter(key => columnFilters[key].size > 0).length;

  // Modal handlers
  const handleInsightsClick = async (vehicle: SIFTVehicle) => {
    // Open modal immediately with basic vehicle info
    setSelectedVehicle(vehicle);
    setIsModalOpen(true);
    setIsLoadingVehicleDetails(true);
    
    try {
      // Fetch full vehicle details including JSON insights in the background
      const fullVehicleData = await siftApiClient.getVehicleByVin(vehicle.vin);
      const fullSiftVehicle = apiVehicleToSIFTVehicle(fullVehicleData);
      
      setSelectedVehicle(fullSiftVehicle);
    } catch (error) {
      console.error("Failed to load vehicle details:", error);
      // Keep the basic vehicle data already set
    } finally {
      setIsLoadingVehicleDetails(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedVehicle(null);
  };

  // Filter and search logic (sorting is now handled by backend)
  const filteredVehicles = useMemo(() => {
    let filtered = displayVehicles;

    // Only apply search filter if we're managing it internally
    if (externalSearchTerm === undefined && internalSearchTerm) {
      filtered = filtered.filter((vehicle: SIFTVehicle) => {
        const searchMatch = [vehicle.seller_name, vehicle.vin, vehicle.model_year, vehicle.make, vehicle.model, vehicle.trim, vehicle.list_status, vehicle.channel, vehicle.facilitation, vehicle.list_days.toString(), vehicle.data_source, vehicle.territory_city, vehicle.territory_state, vehicle.territory_zip]
          .some(value => String(value).toLowerCase().includes(internalSearchTerm.toLowerCase()));
        return searchMatch;
      });
    }

    // Only apply client-side column filtering if NOT managed externally
    // When onColumnFiltersChange is provided, the parent handles filtering via backend
    if (!onColumnFiltersChange && Object.keys(columnFilters).length > 0) {
      filtered = filtered.filter((vehicle: SIFTVehicle) => {
        return Object.entries(columnFilters).every(([columnKey, selectedValues]) => {
          if (selectedValues.size === 0) return true;  // No filter applied for this column
          const value = getVehicleValue(vehicle, columnKey);
          const stringValue = value !== null && value !== undefined ? String(value) : '';
          // If the value IS in the selected set, keep it
          return selectedValues.has(stringValue);
        });
      });
    }

    return filtered;
  }, [displayVehicles, externalSearchTerm, internalSearchTerm, columnFilters, getVehicleValue, onColumnFiltersChange]);

  // Helper to get sort info for a column
  const getSortInfo = (key: string) => {
    const index = externalSortColumns.findIndex(s => s.key === key);
    if (index === -1) return null;
    return { order: externalSortColumns[index].order, priority: index + 1 };
  };

  // Handle column sort click - supports multi-column with Shift key
  const handleSort = (key: string, event: React.MouseEvent) => {
    if (!onSortChange) return;
    
    const existingIndex = externalSortColumns.findIndex(s => s.key === key);
    const isShiftHeld = event.shiftKey;
    
    if (isShiftHeld) {
      // Multi-column sort mode
      if (existingIndex >= 0) {
        // Column already in sort - toggle direction or remove if clicking again with same direction
        const current = externalSortColumns[existingIndex];
        const newOrder = current.order === 'asc' ? 'desc' : 'asc';
        const newColumns = [...externalSortColumns];
        newColumns[existingIndex] = { key, order: newOrder };
        onSortChange(newColumns);
      } else {
        // Add new column to sort
        onSortChange([...externalSortColumns, { key, order: 'asc' }]);
      }
    } else {
      // Single column sort mode (default)
      if (existingIndex >= 0 && externalSortColumns.length === 1) {
        // Toggle direction on existing single column
        const newOrder = externalSortColumns[0].order === 'asc' ? 'desc' : 'asc';
        onSortChange([{ key, order: newOrder }]);
      } else {
        // Replace with new single column sort
        onSortChange([{ key, order: 'asc' }]);
      }
    }
  };

  // Copy all filtered VINs to clipboard
  const handleCopyVins = useCallback(async () => {
    const vins = filteredVehicles.map(v => v.vin).join('\n');
    try {
      await navigator.clipboard.writeText(vins);
      setCopyFeedback(`Copied ${filteredVehicles.length} VINs`);
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (err) {
      console.error('Failed to copy VINs:', err);
      setCopyFeedback('Failed to copy');
      setTimeout(() => setCopyFeedback(null), 2000);
    }
  }, [filteredVehicles]);

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  return (
    <div className="vehicle-data-table">
      <div className="vehicle-data-table-content">
        {/* Table Controls */}
        <div className="table-controls">
        <div className="search-section">
          <input
            type="text"
            placeholder="Search all columns..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && onSearchClick) {
                onSearchClick();
              }
            }}
            className="search-input"
          />
          {onSearchClick && (
            <button
              onClick={onSearchClick}
              className="search-button"
              title="Apply search"
            >
              🔍 Search
            </button>
          )}
          {onClearSearch && (
            <button
              onClick={onClearSearch}
              className="search-clear-button"
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
        
        <div className="table-actions">
          <button
            onClick={handleCopyVins}
            className="copy-vins-button"
            title="Copy all filtered VINs to clipboard"
          >
            {copyFeedback || `📋 Copy ${filteredVehicles.length} VINs`}
          </button>
          {selectedVins.size > 0 && (
            <button
              onClick={handleAssignStatusClick}
              className="assign-status-button"
              title={`Assign status to ${selectedVins.size} selected vehicle${selectedVins.size > 1 ? 's' : ''}`}
            >
              📝 Assign Status ({selectedVins.size})
            </button>
          )}
          {/* Name the active column filters rather than just counting them: Site
              Location is pre-filtered to Offsite by default, and its column is far
              enough right that the only way to discover that was to scroll. */}
          {activeColumnFilters.map(([key, values]) => (
            <button
              key={`acf:${key}`}
              onClick={() => clearColumnFilter(key)}
              className="active-column-filter"
              title={`Remove the ${COLUMN_LABEL[key] ?? key} filter`}
            >
              <span className="active-column-filter-col">{COLUMN_LABEL[key] ?? key}:</span>
              <span className="active-column-filter-val">
                {values.length <= 2 ? values.join(', ') : `${values.length} selected`}
              </span>
              <span className="active-column-filter-x">✕</span>
            </button>
          ))}
          {activeFilterCount > 1 && (
            <button
              onClick={clearAllColumnFilters}
              className="clear-column-filters-button"
              title="Clear all column filters"
            >
              ✕ Clear all
            </button>
          )}
          <span className="results-count">
            {filteredVehicles.length} of {displayVehicles.length} vehicles
          </span>
          {externalSortColumns.length > 1 && (
            <span 
              className="sort-hint clickable" 
              title="Click to clear multi-sort"
              onClick={() => onSortChange && onSortChange([externalSortColumns[0]])}
            >
              Sorted by {externalSortColumns.length} columns (click to clear)
            </span>
          )}
          {externalSortColumns.length <= 1 && (
            <span className="sort-hint">
              Shift+click headers for multi-column sort
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {/* Checkbox column header */}
              <th className="checkbox-col" style={{ width: 40, minWidth: 40 }}>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={el => {
                    if (el) el.indeterminate = isSomeSelected && !isAllSelected;
                  }}
                  onChange={(e) => handleSelectAllChange(e.target.checked)}
                  title={isAllSelected ? "Deselect all" : "Select all"}
                />
              </th>
              {COLUMNS.map((col, index) => {
                const sortInfo = getSortInfo(col.key);
                const columnHasFilter = hasActiveFilter(col.key);
                // Use API-fetched values if available, otherwise fall back to local values
                const uniqueValues = col.filterable 
                  ? (columnDistinctValues[col.key] || getUniqueColumnValues(col.key))
                  : [];
                const isLoadingValues = loadingColumnValues === col.key;
                const truncatedInfo = columnTruncatedInfo[col.key];
                const isTruncated = truncatedInfo?.truncated || false;
                const filteredUniqueValues = uniqueValues.filter(v => 
                  v.toLowerCase().includes(filterSearchTerm.toLowerCase())
                );
                return (
                  <th 
                    key={col.key}
                    style={{ width: columnWidths[col.key], minWidth: 40 }}
                    className={`sortable resizable ${sortInfo ? 'sorted' : ''} ${columnHasFilter ? 'filtered' : ''}`}
                  >
                    <div className="th-content">
                      <div className="th-label-wrapper" onClick={(e) => handleSort(col.key, e)}>
                        <span className="th-label">{col.label}</span>
                        {sortInfo && (
                          <span className="sort-indicator">
                            {sortInfo.order === 'asc' ? '↑' : '↓'}
                            {externalSortColumns.length > 1 && (
                              <span className="sort-priority">{sortInfo.priority}</span>
                            )}
                          </span>
                        )}
                      </div>
                      {col.filterable && (
                        <button 
                          className={`column-filter-btn ${columnHasFilter ? 'active' : ''}`}
                          onClick={(e) => handleFilterClick(e, col.key)}
                          title={`Filter by ${col.label}`}
                        >
                          ▼
                        </button>
                      )}
                    </div>
                    {/* Column Filter Dropdown */}
                    {activeFilterColumn === col.key && (
                      <div 
                        className="column-filter-dropdown" 
                        ref={filterDropdownRef}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="col-filter-header">
                          <input
                            type="text"
                            placeholder={isTruncated ? `Type to search all ${col.label}...` : `Search ${col.label}...`}
                            value={filterSearchTerm}
                            onChange={(e) => handleFilterSearchChange(e.target.value, col.key, isTruncated)}
                            className="col-filter-search"
                            autoFocus
                          />
                        </div>
                        {truncatedInfo?.truncated && (
                          <div className="col-filter-truncated-warning">
                            ⚠️ Showing first 1,000 of {truncatedInfo.total_count.toLocaleString()} values. Use search to filter.
                          </div>
                        )}
                        {/* Select All checkbox */}
                        <div className="col-filter-select-all">
                          <label className="col-filter-option col-filter-option-select-all">
                            <input
                              type="checkbox"
                              checked={pendingSelectAll}
                              onChange={handlePendingSelectAllToggle}
                            />
                            <span className="col-filter-option-text">(Select All)</span>
                          </label>
                        </div>
                        {/* Values list */}
                        <div className="col-filter-list">
                          {isLoadingValues ? (
                            <div className="col-filter-loading">Loading values...</div>
                          ) : filteredUniqueValues.length === 0 ? (
                            <div className="col-filter-no-results">No matches found</div>
                          ) : (
                            filteredUniqueValues.map(value => {
                              // If Select All is checked, all items appear checked
                              // Otherwise, only items in the pending filter set are checked
                              const isSelected = pendingSelectAll || (pendingFilter?.has(value) ?? false);
                              return (
                                <label key={value} className="col-filter-option">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handlePendingFilterToggle(value)}
                                    disabled={pendingSelectAll}
                                  />
                                  <span className={`col-filter-option-text ${pendingSelectAll ? 'disabled' : ''}`}>{value}</span>
                                </label>
                              );
                            })
                          )}
                        </div>
                        {/* Apply/Cancel buttons */}
                        <div className="col-filter-actions">
                          <button 
                            className="col-filter-btn-cancel"
                            onClick={handleCancelFilter}
                          >
                            Cancel
                          </button>
                          <button 
                            className="col-filter-btn-apply"
                            onClick={() => handleApplyFilter(col.key)}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                    {index < COLUMNS.length - 1 && (
                      <div 
                        className="resize-handle"
                        onMouseDown={(e) => handleResizeStart(e, col.key)}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          
          <tbody>
            {filteredVehicles.map((vehicle) => (
              <tr 
                key={vehicle.vin} 
                className={`data-row ${selectedVins.has(vehicle.vin) ? 'selected' : ''}`}
                data-usage-vin={vehicle.vin}
                data-usage-seller-uid={vehicle.seller_id}
                data-usage-section="vin_table"
              >
                {/* Checkbox cell */}
                <td className="checkbox-col">
                  <input
                    type="checkbox"
                    checked={selectedVins.has(vehicle.vin)}
                    onChange={(e) => handleRowCheckboxChange(vehicle.vin, e.target.checked)}
                  />
                </td>
                {/* Status cell */}
                <td title={getStatusDisplayText(vehicle.user_status)}>
                  <span
                    className={getStatusBadgeClass(vehicle.user_status)}
                    title={statusReasons[vehicle.vin] ? `Reason: ${statusReasons[vehicle.vin]}` : undefined}
                  >
                    {getStatusDisplayText(vehicle.user_status)}
                  </span>
                </td>
                {/* Rest of columns */}
                <td title={vehicle.seller_id}>
                  {vehicle.seller_id}
                </td>
                <td title={vehicle.seller_name}>
                  {vehicle.seller_name}
                </td>
                <td 
                  className="vin-link" 
                  title={`Click to view insights for ${vehicle.vin}`}
                  onClick={() => handleInsightsClick(vehicle)}
                >
                  {vehicle.vin}
                </td>
                <td title={String(vehicle.model_year)}>{vehicle.model_year}</td>
                <td title={vehicle.make}>{vehicle.make}</td>
                <td title={vehicle.model}>{vehicle.model}</td>
                <td title={vehicle.trim || '-'}>{vehicle.trim || '-'}</td>
                <td title={vehicle.list_status}>{vehicle.list_status}</td>
                <td title={vehicle.inspect_status}>{vehicle.inspect_status}</td>
                <td title={vehicle.cr_grade != null ? vehicle.cr_grade.toFixed(1) : '-'}>{vehicle.cr_grade != null ? vehicle.cr_grade.toFixed(1) : '-'}</td>
                <td title={vehicle.inspection_channel || '-'}>{vehicle.inspection_channel || '-'}</td>
                <td title={formatDate(vehicle.inspect_time)}>{formatDate(vehicle.inspect_time)}</td>
                <td title={vehicle.channel}>{vehicle.channel}</td>
                <td title={vehicle.facilitation || '-'}>{vehicle.facilitation || '-'}</td>
                <td title={vehicle.site_location || '-'}>{vehicle.site_location || '-'}</td>
                <td title={String(vehicle.list_days)}>{vehicle.list_days}</td>
                <td title={vehicle.days_expired !== null ? String(vehicle.days_expired) : '-'}>{vehicle.days_expired !== null ? vehicle.days_expired : '-'}</td>
                <td>
                  {vehicle.vdp_url ? (
                    <a href={vehicle.vdp_url} target="_blank" rel="noopener noreferrer" className="vdp-link">VDP</a>
                  ) : '-'}
                </td>
                <td title={vehicle.data_source || '-'}>{vehicle.data_source || '-'}</td>
                <td title={vehicle.territory_city || '-'}>{vehicle.territory_city || '-'}</td>
                <td title={vehicle.territory_state || '-'}>{vehicle.territory_state || '-'}</td>
                <td title={vehicle.territory_zip || '-'}>{vehicle.territory_zip || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>

      {/* Vehicle Insights Modal */}
      {selectedVehicle && (
        <VehicleInsightsModal
          vehicle={selectedVehicle}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          isLoading={isLoadingVehicleDetails}
          miaEnabled={miaEnabled}
        />
      )}
      
      {/* Status Assignment Modal */}
      {showStatusModal && (
        <div className="status-modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="status-modal" onClick={(e) => e.stopPropagation()}>
            <div className="status-modal-header">
              <h3>Assign Status</h3>
              <button className="status-modal-close" onClick={() => setShowStatusModal(false)}>×</button>
            </div>
            <div className="status-modal-body">
              <p>Select a status for {selectedVins.size} vehicle{selectedVins.size > 1 ? 's' : ''}:</p>
              <label className="status-reason-label">
                Reason (optional, applies to every selected vehicle)
                <textarea
                  className="status-reason-input"
                  value={statusReason}
                  maxLength={VIN_STATUS_REASON_MAX}
                  rows={2}
                  placeholder="e.g. Client is retailing it on their lot"
                  onChange={(e) => setStatusReason(e.target.value)}
                />
              </label>
              <div className="status-options">
                {USER_STATUS_OPTIONS.map((status) => (
                  <button
                    key={status || 'clear'}
                    className={`status-option-btn ${getStatusBadgeClass(status)}`}
                    onClick={() => handleStatusSelect(status)}
                  >
                    {status}
                  </button>
                ))}
                <button
                  className="status-option-btn status-badge status-unassigned"
                  onClick={() => handleStatusSelect(null)}
                >
                  Clear Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};